import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Tooltip, Chip, Typography, Autocomplete, Collapse,
  Badge, Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { FilterOptions, KpiData, SortField, Order } from '../types';
import { API_BASE } from '../constants';

interface JournalToolbarProps {
  // ── Filter state ──
  selectedMonth: string;
  setSelectedMonth: (v: string) => void;
  selectedPharmacy: string | null;
  setSelectedPharmacy: (v: string | null) => void;
  selectedProduct: string | null;
  setSelectedProduct: (v: string | null) => void;
  selectedSerial: string | null;
  setSelectedSerial: (v: string | null) => void;
  selectedCategory: string;
  setSelectedCategory: (v: string) => void;
  selectedManager: string;
  setSelectedManager: (v: string) => void;
  selectedProducer: string | null;
  setSelectedProducer: (v: string | null) => void;
  selectedPriceGroup: string | null;
  setSelectedPriceGroup: (v: string | null) => void;
  expiryFrom: string;
  setExpiryFrom: (v: string) => void;
  expiryTo: string;
  setExpiryTo: (v: string) => void;

  // ── Options & sort ──
  filterOptions: FilterOptions;
  monthsList: { value: string; label: string }[];
  filterParams: Record<string, string>;
  orderBy: SortField;
  order: Order;

  // ── KPI (для блокировки экспорта) ──
  kpi: KpiData | null;

  // ── Callbacks ──
  onReset: () => void;
  onRefresh: () => void;

  // ── Column Visibility ──
  visibleColumns: Set<string>;
}

/**
 * Панель фильтров и действий журнала.
 *
 * Основная строка: Период, Аптека, кнопка «Ещё фильтры» (со счётчиком
 * активных доп. фильтров) и компактная группа действий (Сброс / Обновить / Excel).
 * Второстепенные фильтры (Товар, Серия, Производитель, Ценовая группа,
 * Категория, Менеджер, Срок годности) спрятаны в раскрывающуюся панель,
 * чтобы тулбар не растягивался на 2–3 строки.
 *
 * Экспорт в Excel: открывает URL напрямую — браузер скачивает XLSX с сервера.
 * @type {import('react').FC<JournalToolbarProps>}
 */
const JournalToolbar: React.FC<JournalToolbarProps> = ({
  selectedMonth, setSelectedMonth,
  selectedPharmacy, setSelectedPharmacy,
  selectedProduct, setSelectedProduct,
  selectedSerial, setSelectedSerial,
  selectedCategory, setSelectedCategory,
  selectedManager, setSelectedManager,
  selectedProducer, setSelectedProducer,
  selectedPriceGroup, setSelectedPriceGroup,
  expiryFrom, setExpiryFrom,
  expiryTo, setExpiryTo,
  filterOptions, monthsList, filterParams, orderBy, order,
  kpi, onReset, onRefresh,
  visibleColumns,
}) => {
  const theme = useTheme();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Количество активных фильтров, спрятанных в «Дополнительных фильтрах»
  const advancedActiveCount = useMemo(() => {
    let n = 0;
    if (selectedProduct) n += 1;
    if (selectedSerial) n += 1;
    if (selectedProducer) n += 1;
    if (selectedPriceGroup) n += 1;
    if (selectedCategory !== 'all') n += 1;
    if (selectedManager !== 'all') n += 1;
    if (expiryFrom || expiryTo) n += 1;
    return n;
  }, [selectedProduct, selectedSerial, selectedProducer, selectedPriceGroup,
    selectedCategory, selectedManager, expiryFrom, expiryTo]);

  const hasAnyChip = selectedPharmacy || selectedProduct || selectedSerial
    || selectedProducer || selectedPriceGroup
    || selectedCategory !== 'all' || selectedManager !== 'all' || expiryFrom || expiryTo;

  const handleExport = () => {
    // Если колонка «ШК» (barcode) выбрана для экспорта —
    // автоматически добавляем рядом «barcodeImage» (картинка штрихкода).
    // Эта колонка существует только в Excel и не отображается в таблице браузера.
    const colsList = Array.from(visibleColumns);
    const barcodeIdx = colsList.indexOf('barcode');
    if (barcodeIdx !== -1) {
      colsList.splice(barcodeIdx + 1, 0, 'barcodeImage');
    }

    const version = process.env.REACT_APP_DOC_VERSION || '1.0.9';
    const params = new URLSearchParams({
      version,
      ...filterParams,
      orderBy: String(orderBy),
      order: String(order),
      columns: colsList.join(','),
    });
    window.open(`${API_BASE}/journal/export?${params.toString()}`, '_blank');
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      {/* ── Основная строка: ключевые фильтры + действия ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', flexShrink: 0 }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Фильтр:</Typography>
        </Box>

        {/* Период */}
        <FormControl size="small" sx={{ minWidth: 155 }}>
          <InputLabel>Период</InputLabel>
          <Select
            value={selectedMonth}
            label="Период"
            onChange={e => setSelectedMonth(e.target.value)}
          >
            <MenuItem value="all">Все периоды</MenuItem>
            {monthsList.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Аптека */}
        <Autocomplete
          size="small" sx={{ minWidth: 180 }}
          options={filterOptions.pharmacies}
          value={selectedPharmacy}
          onChange={(_, v) => { setSelectedPharmacy(v); setSelectedProduct(null); setSelectedSerial(null); }}
          getOptionLabel={o => `Аптека №${o}`}
          renderInput={params => <TextField {...params} label="Аптека" placeholder="Введите номер…" />}
          noOptionsText="Не найдено" clearText="Сбросить" openText="Открыть" closeText="Закрыть"
          ListboxProps={{ style: { maxHeight: 280 } }}
        />

        {/* Переключатель доп. фильтров */}
        <Badge badgeContent={advancedActiveCount} color="primary">
          <Button
            variant={advancedOpen ? 'contained' : 'outlined'}
            color={advancedOpen ? 'primary' : 'inherit'}
            size="small"
            onClick={() => setAdvancedOpen(o => !o)}
            sx={{
              height: 40,
              minWidth: 40,
              width: 40,
              p: 0,
              borderRadius: 1.5,
              borderColor: 'rgba(0, 0, 0, 0.23)',
            }}
          >
            <TuneIcon fontSize="small" />
          </Button>
        </Badge>

        <Box sx={{ flexGrow: 1 }} />

        {/* ── Компактная группа действий ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Tooltip title="Сбросить все фильтры">
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={onReset}
              sx={{
                height: 40,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                borderColor: '#c4c4c4',
                '&:hover': { borderColor: '#b0b0b0', bgcolor: 'action.hover' },
              }}
            >
              Сброс
            </Button>
          </Tooltip>
          <Tooltip title="Обновить данные">
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{
                height: 40,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 2,
              }}
            >
              Обновить
            </Button>
          </Tooltip>
          <Tooltip title="Скачать в Excel (все записи по фильтрам)">
            <span style={{ display: 'inline-flex' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={kpi?.count === 0}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: 'success.main',
                  borderColor: 'success.main',
                  '&:hover': {
                    borderColor: 'success.dark',
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                  },
                }}
              >
                Excel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Чипы активных фильтров ── */}
      {hasAnyChip && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {selectedPharmacy && (
            <Chip
              size="small" label={`Аптека №${selectedPharmacy}`}
              onDelete={() => setSelectedPharmacy(null)}
              sx={{ bgcolor: 'rgba(99,102,241,0.08)', color: '#6366f1', fontWeight: 600 }}
            />
          )}
          {selectedProduct && (
            <Chip
              size="small"
              label={selectedProduct.length > 28 ? selectedProduct.slice(0, 28) + '…' : selectedProduct}
              onDelete={() => { setSelectedProduct(null); setSelectedSerial(null); }}
              sx={{ bgcolor: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}
            />
          )}
          {selectedSerial && (
            <Chip
              size="small"
              label={selectedSerial.length > 22 ? selectedSerial.slice(0, 22) + '…' : selectedSerial}
              onDelete={() => setSelectedSerial(null)}
              sx={{ bgcolor: 'rgba(14,165,233,0.08)', color: '#0284c7', fontWeight: 600 }}
            />
          )}
          {selectedProducer && (
            <Chip
              size="small"
              label={selectedProducer.length > 28 ? selectedProducer.slice(0, 28) + '…' : selectedProducer}
              onDelete={() => setSelectedProducer(null)}
              sx={{ bgcolor: 'rgba(249,115,22,0.08)', color: '#ea580c', fontWeight: 600 }}
            />
          )}
          {selectedPriceGroup && (
            <Chip
              size="small"
              label={selectedPriceGroup.length > 22 ? selectedPriceGroup.slice(0, 22) + '…' : selectedPriceGroup}
              onDelete={() => setSelectedPriceGroup(null)}
              sx={{ bgcolor: 'rgba(168,85,247,0.08)', color: '#9333ea', fontWeight: 600 }}
            />
          )}
          {selectedCategory !== 'all' && (
            <Chip
              size="small" label={selectedCategory}
              onDelete={() => setSelectedCategory('all')}
              sx={{ bgcolor: 'rgba(139,92,246,0.08)', color: '#7c3aed', fontWeight: 600 }}
            />
          )}
          {selectedManager !== 'all' && (
            <Chip
              size="small" label={selectedManager}
              onDelete={() => setSelectedManager('all')}
              sx={{ bgcolor: 'rgba(236,72,153,0.08)', color: '#db2777', fontWeight: 600 }}
            />
          )}
          {(expiryFrom || expiryTo) && (
            <Chip
              size="small" label={`Срок: ${expiryFrom || '?'} — ${expiryTo || '?'}`}
              onDelete={() => { setExpiryFrom(''); setExpiryTo(''); }}
              sx={{ bgcolor: 'rgba(245,158,11,0.08)', color: '#d97706', fontWeight: 600 }}
            />
          )}
        </Box>
      )}

      {/* ── Раскрывающаяся панель доп. фильтров ── */}
      <Collapse in={advancedOpen} timeout={200} unmountOnExit>
        <Divider sx={{ my: 1.25 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {/* Товар */}
          <Autocomplete
            size="small" sx={{ minWidth: 280, maxWidth: 380 }}
            options={filterOptions.products}
            value={selectedProduct}
            onChange={(_, v) => { setSelectedProduct(v); setSelectedSerial(null); }}
            renderInput={params => <TextField {...params} label="Товар" placeholder="Введите наименование…" />}
            noOptionsText="Не найдено" clearText="Сбросить" openText="Открыть" closeText="Закрыть"
            ListboxProps={{ style: { maxHeight: 280 } }}
          />

          {/* Серия */}
          <Autocomplete
            size="small" sx={{ minWidth: 200, maxWidth: 300 }}
            options={filterOptions.serials}
            value={selectedSerial}
            onChange={(_, v) => setSelectedSerial(v)}
            renderInput={params => (
              <TextField
                {...params}
                label="Серия"
                placeholder={selectedProduct ? 'Выберите серию…' : 'сначала выберите товар'}
              />
            )}
            disabled={filterOptions.serials.length === 0 && !selectedProduct}
            noOptionsText="Нет серий" clearText="Сбросить" openText="Открыть" closeText="Закрыть"
            ListboxProps={{ style: { maxHeight: 280 } }}
          />

          {/* Производитель */}
          <Autocomplete
            size="small" sx={{ minWidth: 220, maxWidth: 340 }}
            options={filterOptions.producers}
            value={selectedProducer}
            onChange={(_, v) => setSelectedProducer(v)}
            renderInput={params => <TextField {...params} label="Производитель" placeholder="Введите производителя…" />}
            noOptionsText="Не найдено" clearText="Сбросить" openText="Открыть" closeText="Закрыть"
            ListboxProps={{ style: { maxHeight: 280 } }}
          />

          {/* Ценовая группа */}
          <Autocomplete
            size="small" sx={{ minWidth: 180, maxWidth: 280 }}
            options={filterOptions.priceGroups}
            value={selectedPriceGroup}
            onChange={(_, v) => setSelectedPriceGroup(v)}
            renderInput={params => <TextField {...params} label="Ценовая группа" placeholder="Выберите группу…" />}
            noOptionsText="Не найдено" clearText="Сбросить" openText="Открыть" closeText="Закрыть"
            ListboxProps={{ style: { maxHeight: 280 } }}
          />

          {/* Категория */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Категория</InputLabel>
            <Select
              value={selectedCategory}
              label="Категория"
              onChange={e => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="all">Все категории</MenuItem>
              {filterOptions.categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Менеджер */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Менеджер</InputLabel>
            <Select
              value={selectedManager}
              label="Менеджер"
              onChange={e => setSelectedManager(e.target.value)}
            >
              <MenuItem value="all">Все менеджеры</MenuItem>
              {filterOptions.managers.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Срок годности от/до */}
          <TextField
            size="small" type="date" label="Срок годн. от"
            value={expiryFrom} onChange={e => setExpiryFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
          />
          <TextField
            size="small" type="date" label="Срок годн. до"
            value={expiryTo} onChange={e => setExpiryTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
            inputProps={{ min: expiryFrom || undefined }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default JournalToolbar;
